/**
 * UpdateApplicationModal Component
 *
 * Modal for updating an existing application metadata and actions.
 * Note: applicationCode is immutable and displayed as read-only.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Textarea } from '../../../components/ui/Textarea';
import { Select } from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import { Alert } from '../../../components/ui/Alert';
import { Spinner } from '../../../components/ui/Spinner';
import { ActionsBuilder } from './ActionsBuilder';
import { applicationUpdateSchema } from '../../../lib/validators/application';
import type { ApplicationSanitized, ApplicationUpdateDTO } from '../../../types/application.types';
import { useUpdateApplication } from '../hooks/useUpdateApplication';
import { useRoles } from '../../roles/hooks/useRoles';
import { RoleType } from '../../../types/role.types';
import { getErrorMessage } from '../../../lib/utils/errors';
import { ExecutionMode } from '../../../types/application.types';

interface UpdateApplicationModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenantId: string;
  application: ApplicationSanitized;
}

/**
 * UpdateApplicationModal component
 */
export const UpdateApplicationModal: React.FC<UpdateApplicationModalProps> = ({
  isOpen,
  onClose,
  tenantId,
  application,
}) => {
  const [actions, setActions] = useState<string[]>(application.actions || []);
  const [actionsChanged, setActionsChanged] = useState<boolean>(false);
  const [selectedRoleId, setSelectedRoleId] = useState<string>(application.role?.roleId || '');
  const [roleChanged, setRoleChanged] = useState<boolean>(false);

  // Fetch all roles and filter for APPLICATION type
  const { data: allRoles = [], isLoading: isLoadingRoles } = useRoles(tenantId);
  const applicationRoles = useMemo(
    () => allRoles.filter((role) => role.type === RoleType.APPLICATION),
    [allRoles]
  );

  // Track role changes
  const handleRoleChange = (roleId: string) => {
    setSelectedRoleId(roleId);
    const originalRoleId = application.role?.roleId || '';
    setRoleChanged(roleId !== originalRoleId);
  };

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
    setValue,
  } = useForm<ApplicationUpdateDTO>({
    resolver: zodResolver(applicationUpdateSchema),
    defaultValues: {
      applicationName: application.applicationName,
      actions: application.actions || [],
      description: application.description || '',
      isActive: application.isActive,
      showInLauncher: application.showInLauncher !== undefined ? application.showInLauncher : true,
      menu: application.menu || '',
      submenu: application.submenu || '',
      executionMode: application.executionMode || ExecutionMode.LATEST,
    },
  });

  // Reset form when application changes
  useEffect(() => {
    setActions(application.actions || []);
    setActionsChanged(false);
    setSelectedRoleId(application.role?.roleId || '');
    setRoleChanged(false);
    reset({
      applicationName: application.applicationName,
      actions: application.actions || [],
      description: application.description || '',
      isActive: application.isActive,
      showInLauncher: application.showInLauncher !== undefined ? application.showInLauncher : true,
      menu: application.menu || '',
      submenu: application.submenu || '',
      executionMode: application.executionMode || ExecutionMode.LATEST,
    });
  }, [application, reset]);

  // Track actions changes
  const handleActionsChange = (newActions: string[]) => {
    setActions(newActions);
    setValue('actions', newActions);
    // Check if actions actually changed
    const originalActions = application.actions || [];
    const changed = newActions.length !== originalActions.length ||
      newActions.some((a) => !originalActions.includes(a)) ||
      originalActions.some((a) => !newActions.includes(a));
    setActionsChanged(changed);
  };

  const updateMutation = useUpdateApplication({
    tenantId,
    applicationId: application.applicationId,
    onSuccess: (data) => {
      console.log('Application updated:', data.applicationId);
      handleClose();
    },
    onError: (error) => {
      console.error('Failed to update application:', error);
    },
  });

  const handleClose = () => {
    reset();
    setActions(application.actions || []);
    setActionsChanged(false);
    setSelectedRoleId(application.role?.roleId || '');
    setRoleChanged(false);
    onClose();
  };

  const onSubmit = async (data: ApplicationUpdateDTO) => {
    // Remove empty fields - only include changed values
    const cleanedData: ApplicationUpdateDTO = {};
    if (data.applicationName && data.applicationName !== application.applicationName) cleanedData.applicationName = data.applicationName;
    if (data.description !== undefined && data.description !== application.description) {
      cleanedData.description = data.description;
    }
    if (data.isActive !== undefined && data.isActive !== application.isActive) {
      cleanedData.isActive = data.isActive;
    }
    if (data.showInLauncher !== undefined && data.showInLauncher !== (application.showInLauncher !== undefined ? application.showInLauncher : true)) {
      cleanedData.showInLauncher = data.showInLauncher;
    }
    if (data.menu !== undefined && data.menu !== application.menu) {
      cleanedData.menu = data.menu;
    }
    if (data.submenu !== undefined && data.submenu !== application.submenu) {
      cleanedData.submenu = data.submenu;
    }
    // Include actions if changed
    if (actionsChanged) {
      cleanedData.actions = actions;
    }
    // Include executionMode if changed
    if (data.executionMode && data.executionMode !== (application.executionMode || ExecutionMode.LATEST)) {
      cleanedData.executionMode = data.executionMode;
    }
    // Include roleId if changed (null means remove role)
    if (roleChanged) {
      cleanedData.roleId = selectedRoleId || null;
    }

    // If no changes, just close
    if (Object.keys(cleanedData).length === 0) {
      handleClose();
      return;
    }

    // Update the application
    updateMutation.mutate(cleanedData);
  };

  const hasChanges = isDirty || actionsChanged || roleChanged;

  const isLoading = updateMutation.isPending;
  const error = updateMutation.error;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <Modal.Header>Update Application</Modal.Header>

      <Modal.Body>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Error Alert */}
          {error && (
            <Alert variant="error">
              {getErrorMessage(error)}
            </Alert>
          )}

          {/* Application Code (Read-Only) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Application Code
            </label>
            <div className="flex items-center gap-2">
              <code className="flex-1 px-3 py-2 bg-gray-100 border border-gray-300 rounded-md text-sm text-gray-700 font-mono">
                {application.applicationCode || 'N/A'}
              </code>
              <span className="text-xs text-gray-500 bg-gray-200 px-2 py-1 rounded">
                Immutable
              </span>
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Used in permissions as: {application.applicationCode || 'app'}:action
            </p>
          </div>

          {/* Name Field */}
          <div>
            <Input
              label="Application Name"
              placeholder="My Application"
              {...register('applicationName')}
              error={errors.applicationName?.message}
              disabled={isLoading}
            />
          </div>

          {/* Actions Field */}
          <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
            <h4 className="text-sm font-medium text-gray-900 mb-1">
              Supported Actions
            </h4>
            <p className="text-xs text-gray-500 mb-3">
              Define what actions users can perform with this application. Changes affect role permissions.
            </p>
            <ActionsBuilder
              actions={actions}
              onChange={handleActionsChange}
              disabled={isLoading}
              error={errors.actions?.message}
            />
          </div>

          {/* Resource Role Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Resource Role
            </label>
            {isLoadingRoles ? (
              <div className="flex items-center gap-2 py-2">
                <Spinner size="sm" />
                <span className="text-sm text-gray-500">Loading roles...</span>
              </div>
            ) : (
              <Select
                value={selectedRoleId}
                onChange={(e) => handleRoleChange(e.target.value)}
                disabled={isLoading}
              >
                <option value="">None (no Ketrics resource access)</option>
                {applicationRoles.map((role) => (
                  <option key={role.roleId} value={role.roleId}>
                    {role.roleName} ({role.roleCode})
                  </option>
                ))}
              </Select>
            )}
            {!isLoadingRoles && applicationRoles.length === 0 && (
              <p className="text-sm text-amber-600 mt-1">
                No APPLICATION roles found. Create one in the Roles section first.
              </p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              APPLICATION role that grants this app access to Ketrics resources (volumes, secrets, connections, etc.)
            </p>
          </div>

          {/* Description Field */}
          <div>
            <Textarea
              label="Description"
              placeholder="A brief description of your application"
              {...register('description')}
              error={errors.description?.message}
              disabled={isLoading}
              rows={3}
            />
          </div>

          {/* Menu Field */}
          <div>
            <Input
              label="Menu Category"
              placeholder="e.g., Analytics, CRM, Finance"
              {...register('menu')}
              error={errors.menu?.message}
              disabled={isLoading}
            />
            <p className="mt-1 text-sm text-gray-500">
              Top-level menu category for organizing applications
            </p>
          </div>

          {/* Submenu Field */}
          <div>
            <Input
              label="Submenu Category"
              placeholder="e.g., Reports, Dashboards"
              {...register('submenu')}
              error={errors.submenu?.message}
              disabled={isLoading}
            />
            <p className="mt-1 text-sm text-gray-500">
              Subcategory within the menu
            </p>
          </div>

          {/* Active Status Checkbox */}
          <div>
            <Checkbox
              label="Active"
              helperText="Controls whether this application can be launched and executed"
              {...register('isActive')}
              disabled={isLoading}
            />
          </div>

          {/* Show in Launcher Checkbox */}
          <div>
            <Checkbox
              label="Show in Launcher"
              helperText="Controls visibility in the application launcher. Disable for backend-only apps used for background jobs."
              {...register('showInLauncher')}
              disabled={isLoading}
            />
          </div>

          {/* Execution Mode Toggle */}
          <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
            <h4 className="text-sm font-medium text-gray-900 mb-1">
              Execution Mode
            </h4>
            <p className="text-xs text-gray-500 mb-3">
              Controls how this application consumes deployments.
            </p>

            <div className="space-y-2">
              <label className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-white'
              }`}>
                <input
                  type="radio"
                  value={ExecutionMode.LATEST}
                  {...register('executionMode')}
                  disabled={isLoading}
                  className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                />
                <div>
                  <span className="font-medium text-gray-900">Latest (Auto)</span>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Application automatically runs the most recently deployed code. New deployments are activated immediately.
                  </p>
                </div>
              </label>

              <label className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-white'
              }`}>
                <input
                  type="radio"
                  value={ExecutionMode.CUSTOM}
                  {...register('executionMode')}
                  disabled={isLoading}
                  className="mt-1 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                />
                <div>
                  <span className="font-medium text-gray-900">Custom (Manual)</span>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Application runs a specific deployment you select manually. Use the Deployments modal to activate deployments.
                  </p>
                </div>
              </label>
            </div>
          </div>
        </form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit(onSubmit)}
          loading={isLoading}
          disabled={isLoading || !hasChanges}
        >
          Update Application
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
